import { useTextToSpeechProgressContext } from "@/components/pages/text-to-speech/components/providers/text-to-speech-progress-provider";
import { useTextToSpeechContext } from "@/components/pages/text-to-speech/components/providers/text-to-speech-provider";
import { useCancelTextToSpeech } from "@/components/pages/text-to-speech/hooks/use-cancel-text-to-speech";
import { useSubscriptionLimitationsContext } from "@/providers/subscription-limitations-provider/subscription-limitations-provider";
import { useTtsModelsContext } from "@/providers/tts-models-provider/tts-models-provider";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import { textToSpeechLicenseManager } from "@/services/license/license-managers/text-to-speech-license-manager";
import { useLoggingContext } from "@/providers/logging-provider/logging-provider";

export const useTextToSpeech = () => {
  const progressContext = useTextToSpeechProgressContext();
  const { settings, handleTextChange } = useTextToSpeechContext();
  const { cancelTextToSpeech, isCancelling } = useCancelTextToSpeech();
  const { license, user } = useSubscriptionLimitationsContext();
  const { models } = useTtsModelsContext();
  const params = useParams();
  const id = Array.isArray(params.params) ? params.params[0] : undefined;
  const modelName = id ? decodeURIComponent(id) : undefined;
  const selectedModel = models.find((m) => m.name === modelName);

  const [result, setResult] = useState<string>();
  const [maxTextLength, setMaxTextLength] = useState<number>();

  const logger = useLoggingContext();

  const getFile = async (fileName: string) => {
    const resp = await fetch(
      "/api/text-to-speech/get?" +
        new URLSearchParams({
          fileName: fileName,
        }),
    );
    if (resp.status === 200) {
      return await resp.text();
    }
    return undefined;
  };

  useEffect(() => {
    const loadLimits = async () => {
      try {
        const maxTextLength =
          await textToSpeechLicenseManager.serviceUsingTextLimiter.getLimit(
            user?.id,
            license,
          );
        setMaxTextLength(maxTextLength);
      } catch (e) {
        setMaxTextLength(
          textToSpeechLicenseManager.serviceUsingTextLimiter.getPlanLimit(
            license,
          ),
        );
        logger.logError(e as Error);
      }
    };

    loadLimits();
  }, [license, logger, user?.id]);

  useEffect(() => {
    const fetchResults = async () => {
      const res = await getFile(progressContext.result);
      setResult(res);
    };

    if (progressContext.status === "complete") {
      fetchResults();
    }
  }, [progressContext.status, progressContext.result]);

  return {
    maxTextLength,
    progressContext,
    settings,
    isCancelling,
    handleTextChange,
    cancelTextToSpeech,
    selectedModel,
    result,
  };
};
